import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { DotsThree, DownloadSimpleIcon, FileIcon, GlobeSimpleIcon, VaultIcon } from "@phosphor-icons/react";
import { Button } from "@libs/zapi-ui/ds/components/button";
import { Spinner } from "@libs/zapi-ui/ds/components/spinner";
import { SiteIcon } from "@libs/zapi-ui/ds/components/site-icon";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/ds/components/dropdown-menu";
import { ConfirmDialog } from "@/zapi-frontend/shared/ui/ConfirmDialog/ConfirmDialog";
import { useDocuments } from "@/zapi-frontend/shared/hooks/useDocuments/useDocuments";
import { TDocument } from "@/zapi-frontend/shared/hooks/useDocuments/types";
import { cn } from "@/zapi-frontend/shared/lib/utils";
import { downloadVaultAll } from "@/zapi-frontend/shared/lib/downloadVaultAll";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { downloadDocument } from "@/zapi-frontend/shared/lib/downloadDocument";
import {
  ContextEmpty,
  ContextPage,
  ContextPageHeader,
  FILE_ROW_ACTION,
  FileList,
  FileRow
} from "../ProjectLayout/ui/ContextPage";
import { UploadFilesButton } from "../Repository/_UploadFilesButton";
import { PageLoader } from "@/zapi-frontend/shared/ui/PageLoader";

function fileExtension(doc: TDocument): string {
  if (doc.webUrl) return "LINK";
  const match = /\.([a-z0-9]+)$/i.exec(doc.fileName || "");
  return (match?.[1] || "FILE").toUpperCase();
}

function displayName(doc: TDocument): string {
  if (doc.webUrl) return doc.fileName || doc.webUrl;
  return doc.fileName.replace(/\.[a-z0-9]+$/i, "") || doc.fileName;
}

function realName(doc: TDocument): string {
  if (!doc.webUrl) return doc.fileName;
  try {
    return new URL(doc.webUrl).hostname.replace(/^www\./, "");
  } catch {
    return doc.webUrl;
  }
}

function VaultRow({
  doc,
  projectId,
  onDelete,
  onRetry
}: {
  doc: TDocument;
  projectId: string;
  onDelete: () => void;
  onRetry: () => void;
}) {
  const { t, i18n } = useTranslation(["projectHome", "common"]);
  const [isDownloading, setIsDownloading] = useState(false);
  const isWebLink = !!doc.webUrl;
  const pending = doc.status === "processing" || doc.status === "pending";

  const handleDownload = useCallback(async () => {
    setIsDownloading(true);
    const outcome = await downloadDocument(projectId, doc);
    setIsDownloading(false);
    if (!outcome.done) toast.error(outcome.message ?? t("vaultPage.downloadFailed"));
  }, [doc, projectId, t]);

  const addedDate = new Date(doc.createdAt);
  const added = Number.isNaN(addedDate.getTime())
    ? ""
    : addedDate.toLocaleDateString(i18n.language === "ja" ? "ja-JP" : "en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric"
      });
  const author = [doc.createdBy?.firstName, doc.createdBy?.lastName].filter(Boolean).join(" ");
  const provenance = [
    added && t("vaultPage.addedOn", { date: added }),
    author && t("vaultPage.addedBy", { name: author })
  ]
    .filter(Boolean)
    .join(" ");

  const menu = (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={t("vaultPage.rowActions", { name: doc.fileName })}
            data-testid={`vault-row-${doc.id}-menu-trigger`}
            className={cn(FILE_ROW_ACTION, isDownloading && "opacity-100")}
          />
        }
      >
        {isDownloading ? (
          <Spinner aria-label={t("common:loading")} className="size-5 text-muted-foreground" />
        ) : (
          <DotsThree weight="bold" />
        )}
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" sideOffset={8} className="w-auto min-w-48 rounded-xl p-1.5">
        <DropdownMenuItem data-testid={`vault-row-${doc.id}-download-item`} onClick={handleDownload}>
          {isWebLink ? t("vaultPage.openLink") : t("vaultPage.download")}
        </DropdownMenuItem>
        {doc.status === "error" && (
          <DropdownMenuItem data-testid={`vault-row-${doc.id}-retry-item`} onClick={onRetry}>
            {t("vaultPage.retryProcessing")}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem data-testid={`vault-row-${doc.id}-delete-item`} onClick={onDelete} variant="destructive">
          {t("vaultPage.delete")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  return (
    <FileRow
      glyph={isWebLink ? GlobeSimpleIcon : FileIcon}
      pending={pending}
      title={displayName(doc)}
      testId={`vault-row-${doc.id}`}
      meta={
        pending ? (
          t("vaultPage.processing")
        ) : doc.status === "error" ? (
          <span className="text-destructive">{t("vaultPage.processFailed")}</span>
        ) : (
          <>
            <span className="flex min-w-0 shrink items-center gap-1.5" title={doc.webUrl ?? doc.fileName}>
              {isWebLink && <SiteIcon href={doc.webUrl ?? undefined} className="size-3" />}
              <span className="truncate">{realName(doc)}</span>
            </span>
            {provenance && (
              <>
                <span aria-hidden="true">·</span>
                <span className="shrink-0">{provenance}</span>
              </>
            )}
          </>
        )
      }
      format={fileExtension(doc)}
      actions={menu}
    />
  );
}

export function VaultIsland() {
  const { t } = useTranslation(["projectHome", "common"]);
  const { id } = useParams();
  const projectId = useMemo(() => (typeof id === "string" ? id : ""), [id]);

  const {
    documents,
    isLoading,
    createDocument,
    createWebLink,
    deleteDocument,
    isDocumentsPending,
    isDocumentsProcessing,
    uploadingDocuments,
    setUploadingDocuments,
    retryJuicing
  } = useDocuments(projectId, undefined, 3000);

  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);

  const handleDownloadAll = useCallback(async () => {
    setIsDownloadingAll(true);
    const [, error] = await tryCatch(downloadVaultAll(projectId));
    if (error) toast.error(t("vaultPage.downloadAllFailed"));
    setIsDownloadingAll(false);
  }, [projectId, t]);

  const handleConfirmDelete = useCallback(() => {
    if (deleteId === null) return;
    const id = deleteId;
    setDeleteId(null);
    deleteDocument(id);
  }, [deleteId, deleteDocument]);

  return (
    <ContextPage testId="vault-island">
      <ContextPageHeader
        title={t("sidebar.vault")}
        actions={
          <>
            <Button
              type="button"
              variant="outline"
              className="shadow-none rounded-full"
              onClick={handleDownloadAll}
              disabled={isDownloadingAll || documents.length === 0}
              data-testid="vault-page-download-all"
            >
              {isDownloadingAll ? (
                <>
                  <Spinner data-icon="inline-start" aria-label={t("common:loading")} />
                  {t("vaultPage.downloading")}
                </>
              ) : (
                <>
                  <DownloadSimpleIcon className="size-4" />
                  {t("vaultPage.downloadAll")}
                </>
              )}
            </Button>
            <span className="[&_button]:rounded-full">
              <UploadFilesButton
                appearance="design-system"
                variant="header"
                createDocument={(file: File) => createDocument(file)}
                createWebLink={(url: string) => createWebLink(url)}
                setUploadingDocuments={setUploadingDocuments}
                isDocumentsPending={isDocumentsPending}
                isDocumentsProcessing={isDocumentsProcessing}
                existingWebUrls={documents.map((d) => d.webUrl)}
              />
            </span>
          </>
        }
      />
      {isLoading && documents.length === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <PageLoader />
        </div>
      ) : documents.length === 0 && uploadingDocuments.length === 0 ? (
        <ContextEmpty icon={VaultIcon} title={t("vaultPage.empty")} description={t("vaultPage.emptyDescription")} />
      ) : (
        <FileList>
          {documents.map((doc) => (
            <VaultRow
              key={doc.id}
              doc={doc}
              projectId={projectId}
              onDelete={() => setDeleteId(doc.id)}
              onRetry={() => retryJuicing(projectId, doc.id.toString())}
            />
          ))}
          {uploadingDocuments.length > 0 && (
            <FileRow
              glyph={FileIcon}
              pending
              title={uploadingDocuments.join(", ")}
              meta={t("vaultPage.uploadingMeta")}
            />
          )}
        </FileList>
      )}

      <ConfirmDialog
        isOpen={deleteId !== null}
        onClose={() => setDeleteId(null)}
        onConfirm={handleConfirmDelete}
        title={t("vaultPage.deleteTitle")}
        description={t("vaultPage.deleteDescription")}
        confirmText={t("vaultPage.delete")}
        cancelText={t("common:cancel")}
      />
    </ContextPage>
  );
}
